/**
 * KRA Photo Journal – FilenameBuilder
 *
 * Shared chip UI used by both the Drive lightbox bottom bar and the
 * grid-thumbnail "download with custom name" modal. Renders one chip row
 * per category (radio behaviour — at most one slug active per category)
 * plus a single optional custom keyword slot, and previews the resulting
 * filename live.
 *
 * Stateless on purpose — caller owns `selection` so the same builder can
 * back the lightbox flow and the modal flow without sharing state.
 */

import { useState, useCallback } from "react";
import {
  CATEGORY_DEFINITIONS,
  slugifyCustom,
  type CategoryDef,
  type FilenameSelection,
  type SlugCategory,
} from "../lib/seo-filename.js";
import { cn } from "../lib/utils.js";

type MetadataCategory = Exclude<SlugCategory, "custom">;

interface Props {
  selection: FilenameSelection;
  onSelectionChange: (next: FilenameSelection) => void;
  /** The composed filename slug (without extension) plus the extension to
   *  show in the preview line. Null slug renders the empty-state hint. */
  previewSlug: string | null;
  previewExtension?: string;
  /** Compact variant tightens spacing for the lightbox bottom-bar context.
   *  Default `false` renders a roomier modal-friendly layout. */
  compact?: boolean;
}

export function FilenameBuilder({
  selection,
  onSelectionChange,
  previewSlug,
  previewExtension = "webp",
  compact = false,
}: Props) {
  const [customInputOpen, setCustomInputOpen] = useState(false);
  const [customInputValue, setCustomInputValue] = useState(selection.custom ?? "");

  const setCategory = useCallback(
    (category: MetadataCategory, slug: string) => {
      const next: FilenameSelection = { ...selection };
      next[category] = selection[category] === slug ? null : slug;
      onSelectionChange(next);
    },
    [selection, onSelectionChange],
  );

  const submitCustomChip = useCallback(() => {
    const slug = slugifyCustom(customInputValue);
    onSelectionChange({ ...selection, custom: slug });
    setCustomInputValue(slug ?? "");
    setCustomInputOpen(false);
  }, [customInputValue, selection, onSelectionChange]);

  const clearCustomChip = useCallback(() => {
    onSelectionChange({ ...selection, custom: null });
    setCustomInputValue("");
  }, [selection, onSelectionChange]);

  const filenamePreview = previewSlug ? `${previewSlug}.${previewExtension}` : null;

  return (
    <div className={cn("flex flex-col", compact ? "gap-1.5" : "gap-3")}>
      {CATEGORY_DEFINITIONS.map((def) => (
        <CategoryRow
          key={def.category}
          def={def}
          selectedSlug={selection[def.category]}
          onSelect={(slug) => setCategory(def.category, slug)}
          compact={compact}
        />
      ))}

      {/* Custom keyword slot */}
      <div className={cn("flex items-center gap-2", compact ? "text-[10px]" : "text-xs")}>
        <span className={cn("flex-shrink-0 text-white/50", compact ? "min-w-[80px]" : "min-w-[110px]")}>
          ✏️ Tự thêm
        </span>
        <div className="flex flex-wrap items-center gap-1.5">
          {selection.custom && !customInputOpen && (
            <span
              className={cn(
                "inline-flex items-stretch rounded-full overflow-hidden border",
                "bg-white/95 border-white/95",
              )}
            >
              <button
                type="button"
                onClick={() => {
                  setCustomInputValue(selection.custom ?? "");
                  setCustomInputOpen(true);
                }}
                className="px-2.5 py-1 text-[11px] font-mono text-black"
                title="Bấm để sửa từ khóa"
              >
                {selection.custom}
              </button>
              <button
                type="button"
                onClick={clearCustomChip}
                className="px-1.5 text-[10px] text-black/55 hover:text-black border-l border-black/15"
                title="Xóa"
              >
                ✕
              </button>
            </span>
          )}

          {customInputOpen ? (
            <input
              autoFocus
              type="text"
              value={customInputValue}
              onChange={(e) => setCustomInputValue(e.target.value)}
              onBlur={submitCustomChip}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); submitCustomChip(); }
                if (e.key === "Escape") { setCustomInputValue(selection.custom ?? ""); setCustomInputOpen(false); }
              }}
              placeholder="từ khóa…"
              maxLength={40}
              className={cn(
                "px-2.5 py-1 rounded-full text-[11px] font-mono",
                "bg-white/10 text-white placeholder:text-white/40 border border-white/30",
                "outline-none focus:border-white/60 focus:bg-white/15",
                "w-32",
              )}
            />
          ) : (
            !selection.custom && (
              <button
                type="button"
                onClick={() => setCustomInputOpen(true)}
                className={cn(
                  "px-2.5 py-1 rounded-full text-[11px] font-mono",
                  "bg-transparent text-[#E8AAA0] hover:text-white",
                  "border border-dashed border-[#E8AAA0]/60 hover:border-white",
                  "transition-colors",
                )}
                title="Thêm từ khóa tùy chỉnh"
              >
                + Thêm
              </button>
            )
          )}
        </div>
      </div>

      {/* Filename preview */}
      <div className={cn("border-t border-white/10", compact ? "pt-1.5" : "pt-2")}>
        <p className="font-mono text-[11px] text-white/75 break-all leading-snug">
          {filenamePreview ?? (
            <span className="text-white/35">
              — chọn ít nhất 1 chip để tạo tên file —
            </span>
          )}
        </p>
      </div>
    </div>
  );
}

function CategoryRow({
  def,
  selectedSlug,
  onSelect,
  compact,
}: {
  def: CategoryDef;
  selectedSlug: string | null;
  onSelect: (slug: string) => void;
  compact: boolean;
}) {
  return (
    <div className={cn("flex items-start gap-2", compact ? "text-[10px]" : "text-xs")}>
      <span className={cn("flex-shrink-0 text-white/50 leading-tight pt-1", compact ? "min-w-[80px]" : "min-w-[110px]")}>
        {def.emoji} {def.heading}
      </span>
      <div className="flex flex-wrap items-center gap-1.5">
        {def.options.map((opt) => {
          const enabled = selectedSlug === opt.slug;
          return (
            <button
              key={opt.slug}
              type="button"
              onClick={() => onSelect(opt.slug)}
              className={cn(
                "px-2.5 py-1 rounded-full text-[11px] font-mono transition-colors border",
                enabled
                  ? "bg-white/95 text-black border-white/95"
                  : "bg-transparent text-white/55 border-white/20 hover:border-white/45 hover:text-white/85",
              )}
              title={opt.label}
            >
              {opt.slug}
            </button>
          );
        })}
      </div>
    </div>
  );
}
